import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Globe, Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useLanguage } from "../../context/LanguageContext";
import { ASSETS } from "../../mock";

const LANGS = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "pt", label: "Português" },
];

const Header = () => {
  const { lang, changeLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: t.nav.products, href: "#productos" },
    { label: t.nav.benefits, href: "#beneficios" },
    { label: t.nav.payfac, href: "#payfac" },
    { label: t.nav.industries, href: "#industrias" },
    { label: t.nav.pricing, href: "#precios" },
    { label: t.nav.contact, href: "#contacto" },
  ];

  const scrollTo = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <a href="#inicio" onClick={(e) => scrollTo(e, "#inicio")} className="flex items-center shrink-0">
            <img src={ASSETS.logo} alt="RapidPay" className="h-9 md:h-10 w-auto" />
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                className="text-[14px] font-medium text-black/80 hover:text-black transition-colors duration-200 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#0fb013] group-hover:w-full transition-[width] duration-300" />
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="lang-switcher"
                  className="flex items-center gap-1.5 text-[13px] font-semibold text-black/70 hover:text-black px-3 py-2 rounded-full hover:bg-black/5 transition-colors duration-200"
                >
                  <Globe size={16} />
                  {lang.toUpperCase()}
                  <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => changeLang(l.code)}
                    className={`cursor-pointer text-[13px] ${lang === l.code ? "font-bold text-[#0fb013]" : ""}`}
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <a
              href="https://portal.rapidpaypro.com/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="login-button"
              className="text-[13px] font-bold text-black px-5 py-2.5 rounded-full border-2 border-black hover:bg-black hover:text-white transition-colors duration-200"
            >
              {t.nav.login}
            </a>
            <Link
              to="/registro"
              data-testid="signup-button"
              className="text-[13px] font-bold text-black px-5 py-2.5 rounded-full bg-[#0fe814] hover:bg-[#0fb013] transition-colors duration-200 shadow-[0_4px_14px_rgba(15,232,20,0.35)]"
            >
              {t.nav.signup}
            </Link>
          </div>

          {/* Mobile toggles */}
          <div className="flex lg:hidden items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 text-[13px] font-semibold text-black/70 px-2 py-2 rounded-full hover:bg-black/5">
                  <Globe size={16} />
                  {lang.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[140px]">
                {LANGS.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => changeLang(l.code)}
                    className={`cursor-pointer text-[13px] ${lang === l.code ? "font-bold text-[#0fb013]" : ""}`}
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-md hover:bg-black/5"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-black/5 shadow-lg">
          <nav className="flex flex-col px-5 py-4 gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                className="text-[15px] font-medium text-black/80 py-3 border-b border-black/5"
              >
                {link.label}
              </a>
            ))}
            <div className="flex gap-3 pt-4">
              <a
                href="https://portal.rapidpaypro.com/login"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-center text-[13px] font-bold text-black px-5 py-3 rounded-full border-2 border-black"
              >
                {t.nav.login}
              </a>
              <Link
                to="/registro"
                className="flex-1 text-center text-[13px] font-bold text-black px-5 py-3 rounded-full bg-[#0fe814]"
              >
                {t.nav.signup}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
